import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import { defu } from 'defu';
import type {
  AvailableRouterMethod,
  NitroFetchOptions,
  NitroFetchRequest,
} from 'nitropack';
import { HT_APP_ID, HT_PLATFORM } from '~/utils/constant';
import { OJ_API_BASE } from '~/utils/environment';
import { UserService } from './user.service';
import { ZoneService } from './zone.service';

interface NewFetchOptions<R extends NitroFetchRequest>
  extends NitroFetchOptions<R> {
  rawResponse?: boolean;
}

@Injectable()
export class HttpService {
  @Inject(forwardRef(() => UserService))
  private userService!: UserService;

  @Inject(forwardRef(() => ZoneService))
  private zoneService!: ZoneService;

  public COMMON_HEADERS = {
    HT_PLATFORM: HT_PLATFORM,
    HT_SYSTEM: 'web',
    HT_VERSION: '1.0.0',
    app_id: HT_APP_ID,
  };

  private getCommonHeaders(inputHeaders: any, isSameSite: boolean) {
    const headers = inputHeaders || {};
    const { token } = this.userService;

    if (token) {
      headers['Authorization'] = token;
    }

    if (isSameSite) {
      // 核桃OJ的接口统一增加zone头部
      headers['Hetao-Oj-Zone'] = this.zoneService.zone;
    }

    return headers;
  }

  private request<
    T = unknown,
    R extends NitroFetchRequest = NitroFetchRequest,
    O extends NewFetchOptions<R> = NewFetchOptions<R>,
  >(url: R, options?: O) {
    // 不是以http开头，也就是相对路径，需要加上OJ_API_BASE
    const isSameSite = (url as string).indexOf('http') !== 0;
    // 接口默认使用网站域名，除非业务已经指定域名
    const baseURL = isSameSite ? OJ_API_BASE : void 0;

    const defaults = {
      mode: 'cors',
      credentials: 'omit',
      baseURL,
      headers: this.getCommonHeaders(options?.headers, isSameSite),

      onResponse: ({ response }) => {
        if (!options?.rawResponse) {
          // 如果强制返回raw response则不做任何处理，由业务自己处理异常
          const responseData = response._data;
          const { errCode, errMsg, errcode, errmsg } = responseData;
          const errCode2 = errCode === void 0 ? errcode : errCode;
          const errMsg2 = errMsg === void 0 ? errmsg : errMsg;

          if (errCode2 === 0) {
            // 接口正常返回，则直接返回data数据
            response._data = responseData.data;
          } else {
            // 接口正常返回，但是业务逻辑异常
            throw responseData;
          }
        }
      },

      onRequestError: ({ error }) => {
        // showErrorMsg(`Client error: ${error.message}`);
      },

      onResponseError: ({ response }) => {
        // showErrorMsg(`Server error: ${response.status} ${response.body}`);
      },
    } as O;

    // for nice deep defaults, please use unjs/defu
    const params = defu(options, defaults);

    return $fetch<T>(url, params);
  }

  public get<
    T = unknown,
    R extends NitroFetchRequest = NitroFetchRequest,
    O extends NewFetchOptions<R> = NewFetchOptions<R>,
  >(url: R, options: O = {} as O) {
    options.method = 'GET' as AvailableRouterMethod<R>;
    return this.request<T>(url, options);
  }

  public post<
    T = unknown,
    R extends NitroFetchRequest = NitroFetchRequest,
    O extends NewFetchOptions<R> = NewFetchOptions<R>,
  >(url: R, options: O = {} as O) {
    options.method = 'POST' as AvailableRouterMethod<R>;
    return this.request<T>(url, options);
  }

  public put<
    T = unknown,
    R extends NitroFetchRequest = NitroFetchRequest,
    O extends NewFetchOptions<R> = NewFetchOptions<R>,
  >(url: R, options: O = {} as O) {
    options.method = 'PUT' as AvailableRouterMethod<R>;
    return this.request<T>(url, options);
  }

  public delete<
    T = unknown,
    R extends NitroFetchRequest = NitroFetchRequest,
    O extends NewFetchOptions<R> = NewFetchOptions<R>,
  >(url: R, options: O = {} as O) {
    options.method = 'DELETE' as AvailableRouterMethod<R>;
    return this.request<T>(url, options);
  }
}
